/** The fields every material form shares: its type and its audience, both chosen on purpose (no default). */

import { MATERIAL_TYPES, type MaterialAudience, type MaterialType } from '@/api/types/materials';
import { useSession } from '@/session/SessionContext';

export function MaterialTypeField({
  id,
  value,
  onChange,
}: {
  id: string;
  value: MaterialType | '';
  onChange: (value: MaterialType) => void;
}) {
  return (
    <div className="field">
      <label htmlFor={id}>Material type</label>
      <select id={id} required value={value} onChange={(e) => onChange(e.target.value as MaterialType)}>
        <option value="" disabled>
          Choose a type
        </option>
        {MATERIAL_TYPES.map((t) => (
          <option key={t.value} value={t.value}>
            {t.label}
          </option>
        ))}
      </select>
    </div>
  );
}

const AUDIENCES: { value: MaterialAudience; label: string; hint: string }[] = [
  { value: 'shareable', label: 'Client-shareable', hint: 'Anyone who reads the asset sees it, and it may go to clients.' },
  { value: 'internal', label: 'Internal only', hint: 'Only people who may read internal content see it.' },
];

/** Radio buttons with nothing chosen at first: who may see a material is never assumed. */
export function AudienceField({
  name,
  value,
  onChange,
}: {
  name: string;
  value: MaterialAudience | '';
  onChange: (value: MaterialAudience) => void;
}) {
  const { can } = useSession();
  const internal = can('asset.view.internal');
  const offered = internal ? AUDIENCES : AUDIENCES.filter((a) => a.value === 'shareable');
  return (
    <fieldset className="field">
      <legend className="label">Audience</legend>
      {offered.map((a) => (
        <label key={a.value} className="check">
          <input type="radio" name={name} value={a.value} required checked={value === a.value} onChange={() => onChange(a.value)} />
          <span className="ck-body">
            {a.label}
            <span className="hint">{a.hint}</span>
          </span>
        </label>
      ))}
      {!internal && <span className="hint">You can add client-shareable materials only.</span>}
    </fieldset>
  );
}
